import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Row, Col } from "react-bootstrap";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";

import { getApiCall, postApiCall } from "../../../service/httpCall";
import Loader from "../../../components/partials/components/loader";
import { DeleteIcon } from "../../../components/setting/elements";
import RenderDeleteModal from "../../../components/partials/components/delete-modal";
import LottieAnimation from "../../../components/lottie/lottie";
import { useAuth, errorCheck } from "../auth/auth-utils";
import { getUser } from "../../../utilities/setting";
import { USER_ROLES, MODULE_ACL, ROUTES } from "../../../utilities/constant";
import {
  API_MOH_LIST,
  API_MOH_DELETE,
  API_MOH_UPDATE_STATUS,
} from "../../../service/url";
import "../../../assets/custom/css/custom.css";

const MOH_STATUS_LABEL = {
  0: "Pending",
  1: "Approved",
  2: "Rejected",
};

const MohManagement = () => {
  const [mohList, setMohList] = useState([]);
  const [originalDataList, setOriginalDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");

  const [selectedData, setSelected] = useState([]);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(undefined);
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [showToastModal, setShowToastModal] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [reloadTrigger, setReloadTrigger] = useState(false);
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [acl, setAcl] = useState({});
  const [, setToastProps] = useState(null);
  const [, setShowToast] = useState(false);

  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const user = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      try {
        setAcl(JSON.parse(user.acl_settings.module_operations));
      } catch (e) {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    fetchMoh();
  }, [reloadTrigger]);

  useEffect(() => {
    if (searchValue && searchValue.trim() !== "") {
      const q = searchValue.trim().toLowerCase();
      setMohList(originalDataList.filter((d) => d.moh_name?.toLowerCase().includes(q)));
    } else {
      setMohList(originalDataList);
    }
  }, [searchValue, originalDataList]);

  // React to ACL refresh elsewhere (matches manage-prompts pattern).
  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === "string"
              ? JSON.parse(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setAcl(parsed);
        } catch (e) {
          // ignore
        }
      }
    };
    window.addEventListener("acl-refreshed", onAclRefreshed);
    return () => window.removeEventListener("acl-refreshed", onAclRefreshed);
  }, []);

  const fetchMoh = () => {
    setLoading(true);
    getApiCall(API_MOH_LIST)
      .then((resp) => {
        const arr = Array.isArray(resp) ? resp : resp?.data || [];
        const list = arr.map((item, idx) => {
          const groups = Array.isArray(item.allocatedCallGroups)
            ? item.allocatedCallGroups
            : [];
          return {
            ...item,
            key: idx + 1,
            status: MOH_STATUS_LABEL[item.moh_status] || "Pending",
            allocated_groups_label: groups.length
              ? groups.map((g) => g.group_name).join(", ")
              : "-",
            allocated_count: groups.length,
          };
        });
        setOriginalDataList(list);
        setMohList(list);
      })
      .catch((err) => toast.warning(`Failed to load MOH list: ${err}`))
      .finally(() => setLoading(false));
  };

  const showToasts = (msg) => {
    setToastMessage(msg);
    setShowToastModal(true);
    setTimeout(() => setShowToastModal(false), 3000);
  };

  const handleApprove = (id) => {
    postApiCall(API_MOH_UPDATE_STATUS.replace(":id", id), { status: 1 })
      .then(() => {
        showToasts("MOH Approved");
        setReloadTrigger((p) => !p);
      })
      .catch((err) => toast.error(err));
  };

  const handleReject = (id) => {
    postApiCall(API_MOH_UPDATE_STATUS.replace(":id", id), { status: 2 })
      .then(() => {
        showToasts("MOH Rejected");
        setReloadTrigger((p) => !p);
      })
      .catch((err) => toast.error(err));
  };

  const handleListen = (url) => {
    setAudioUrl(url);
    setIsModalVisible(true);
  };
  const handleModalClose = () => {
    setIsModalVisible(false);
    setAudioUrl("");
  };

  const handleSelectAll = () => {
    if (selectedData.length === mohList.length) setSelected([]);
    else setSelected(mohList.map((m) => m.id));
  };
  const handleCheckboxToggle = (id) => {
    setSelected(
      selectedData.includes(id)
        ? selectedData.filter((x) => x !== id)
        : [...selectedData, id]
    );
  };

  const handleMultiDelete = () => {
    setIsMultiDelete(true);
    setEnableDeleteModal(true);
  };
  const handleDelete = (id) => {
    checkAuth(setToastProps, setShowToast);
    setIsMultiDelete(false);
    setEnableDeleteModal(true);
    setDeleteId(id);
  };

  useEffect(() => {
    if (!confirmDelete) return;
    const ids = isMultiDelete ? selectedData : [deleteId];
    postApiCall(API_MOH_DELETE, { mohIds: ids })
      .then((resp) => {
        toast.success(resp);
        setSelected([]);
        setEnableDeleteModal(false);
        setDeleteId(undefined);
        setConfirmDelete(false);
        fetchMoh();
      })
      .catch((err) => {
        if (typeof err !== "object" && err.includes("TokenExpiredError")) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.warning(`Unable to delete MOH: ${err}`);
        }
        setConfirmDelete(false);
      });
  }, [confirmDelete]);

  const downloadFile = async (fileUrl, filename) => {
    try {
      const resp = await fetch(fileUrl);
      if (!resp.ok) throw new Error("Failed to fetch file");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || fileUrl.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download failed", e);
    }
  };

  const rowClassName = (data) => ({
    "highlighted-row": selectedData.includes(data.id),
  });

  const canDelete = isSuperAdmin || acl?.[MODULE_ACL?.MOH_MANAGEMENT]?.delete;
  const canAdd = isSuperAdmin || acl?.[MODULE_ACL?.MOH_MANAGEMENT]?.add;

  const actionBodyTemplate = (rowData) => {
    const isApproved = rowData.status === "Approved";
    const isRejected = rowData.status === "Rejected";
    const isDeleted =
      rowData?.is_deleted === true ||
      Number(rowData?.is_deleted) === 1 ||
      Boolean(rowData?.deleted_at);

    return (
      <div className="d-flex justify-space-between gap-2">
        <button
          className="btn-icons m-1"
          onClick={() => downloadFile(rowData.moh_url, rowData.moh_name)}
          style={{
            marginLeft: "8px",
            border: "none",
            background: "none",
            padding: "0",
          }}
        >
          <i className="fa fa-download" style={{ color: "blue", fontSize: "24px" }} />
        </button>
        <Button
          label="Listen"
          icon="pi pi-play"
          className="listen"
          onClick={() => handleListen(rowData.moh_url)}
        />
        {isSuperAdmin && (
          <>
            <Button
              label="Approve"
              className={`approve ${isApproved ? "disabled-btn" : ""}`}
              onClick={() => !isApproved && handleApprove(rowData.id)}
              disabled={isApproved}
            />
            <Button
              label="Reject"
              className={`reject ${isRejected ? "disabled-btn" : ""}`}
              onClick={() => !isRejected && handleReject(rowData.id)}
              disabled={isRejected}
            />
          </>
        )}
        {canDelete && !isDeleted && (
          <DeleteIcon handleCallback={() => handleDelete(rowData?.id)} />
        )}
      </div>
    );
  };

  if (loading) return <Loader />;

  return (
    <>
      <div>
        <div className="card" style={{ marginTop: "20px" }}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title">MOH Management</h4>
            <div className="d-flex justify-content-flex-end align-items-center">
              {canAdd && (
                <Button
                  label="+ Add MOH"
                  onClick={() => navigate(`/${ROUTES.VOICE_LIBRARY.ADD_MOH}`)}
                  className="p-button-active p-button-sm"
                  style={{ marginBottom: "10px", marginLeft: "10px" }}
                />
              )}
              {canDelete && (
                <Button
                  label="Delete Selected"
                  onClick={handleMultiDelete}
                  className="p-button-danger p-button-sm"
                  style={{ marginBottom: "10px", marginLeft: "10px" }}
                  disabled={selectedData.length === 0}
                />
              )}
            </div>
          </div>

          <div className="card-body">
            <Row className="mb-3 px-3 align-items-center">
              <Col md={4}>
                <input
                  className="form-control"
                  style={{ height: "45px", border: "1px solid #ced4da", fontSize: "15px" }}
                  placeholder="Search by Name..."
                  type="text"
                  value={searchValue || ""}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Button
                  label="Refresh"
                  className="p-button-primary"
                  style={{ height: "45px", width: "120px" }}
                  onClick={() => setReloadTrigger((p) => !p)}
                />
              </Col>
            </Row>

            <div className="scroll-container">
              <DataTable
                value={mohList}
                paginator
                rows={10}
                tableStyle={{ minWidth: "60rem" }}
                rowClassName={rowClassName}
              >
                <Column
                  header={
                    <Checkbox
                      checked={selectedData.length === mohList.length && mohList.length > 0}
                      onChange={handleSelectAll}
                    />
                  }
                  headerStyle={{ width: "3rem" }}
                  body={(rowData) => (
                    <Checkbox
                      checked={selectedData.includes(rowData.id)}
                      onChange={() => handleCheckboxToggle(rowData.id)}
                    />
                  )}
                />
                <Column field="moh_name" header="Name" />
                <Column
                  header="Created Date"
                  body={(r) =>
                    r.created_at
                      ? new Date(r.created_at).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "-"
                  }
                />
                <Column field="moh_url" header="File URL" />
                <Column field="moh_duration" header="Duration (s)" />
                <Column
                  field="allocated_groups_label"
                  header="Allocated To (Call Groups)"
                />
                <Column field="moh_type" header="Type" />
                <Column field="status" header="Status" />
                <Column field="creator.name" header="Created By" />
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ textAlign: "center", width: "16rem" }}
                />
              </DataTable>
            </div>
          </div>
        </div>

        <RenderDeleteModal
          show={enableDeleteModal}
          setShow={setEnableDeleteModal}
          setConfirmDelete={setConfirmDelete}
          isMultiDelete={isMultiDelete}
          clearSelectedData={() => setSelected([])}
        />
      </div>

      {/* Audio playback dialog */}
      <Dialog
        header="MOH Playback"
        visible={isModalVisible}
        style={{
          width: "40vw",
          position: "absolute",
          top: "10px",
          left: "50%",
          transform: "translateX(-50%)",
        }}
        onHide={handleModalClose}
      >
        <audio controls style={{ width: "100%" }}>
          <source src={audioUrl} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      </Dialog>

      {/* Toast modal */}
      <Modal show={showToastModal} centered onHide={() => setShowToastModal(false)} size="sm">
        <Modal.Body className="text-center">
          <h5>{toastMessage}</h5>
          <LottieAnimation />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default MohManagement;
